import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { fetchCsvExport } from '@/lib/utils/exports'
import { QueryHandlers, stringifyValues } from '@/lib/utils/queries'
import { extractZodIssues, getFirstZodErrorMessage } from '@/lib/utils/zodErrorHelpers'
import { getApi } from '@/stores/api'
import { formatDateOnly } from '@/utils/DateOnlyHelpers'
import { CreateDistributionMarginMix, UpdateDistributionMarginMix } from '@repo/backend/crt'
import { DISTRIBUTION_MARGIN_MIX_CREATE_ERROR_TYPES, PRODUCT_CATEGORIES } from '@repo/constants'
import { toastClassName } from '@repo/ui/lib/constants'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { useMaterialGroups } from './useMaterialGroups'

export const distributionMarginMixSearchSchema = z.object({
  keyword: z.string().optional(),
  ...datatableSearchSchema.shape,
})

export type DistributionMarginMixSearch = z.infer<typeof distributionMarginMixSearchSchema>
export const distributionMarginMixDetailsSearchSchema = z.object({
  keyword: z.string().optional(),
  effectiveDateFrom: z.coerce.date().optional(),
  effectiveDateTo: z.coerce.date().optional(),
  distributor: z.array(z.string()).optional(),
  productCategory: z.array(z.enum(PRODUCT_CATEGORIES)).optional(),
  materialGroupCode: z.array(z.string()).optional(),
  productHierarchyCode: z.array(z.string()).optional(),
  ...datatableSearchSchema.shape,
})
export type DistributionMarginMixDetailsSearch = z.infer<
  typeof distributionMarginMixDetailsSearchSchema
>

export type DistributionMarginMixList = Awaited<ReturnType<typeof getDistributionMarginMixList>>
export type DistributionMarginMixItem = DistributionMarginMixList['results'][number]
export type DistributionMarginMixWithDetails = Awaited<
  ReturnType<typeof getDistributionMarginMixById>
>

type UseCreateArgs = {
  queries: DistributionMarginMixSearch
  handleOnSuccess: (id: number) => void
}

type UseUpdateArgs = QueryHandlers & {
  distributionMarginMixId: number
}

type UseDeleteArgs = QueryHandlers & {
  distributionMarginMixIds: number[]
  queries: DistributionMarginMixSearch
}

async function getCurrentDistributionMarginMix(crtPeriod?: string, signal?: AbortSignal) {
  const api = getApi()
  const query = crtPeriod ? { crtPeriod } : {}
  const res = await api.crt['distribution-margin-mix'].current.$get({ query }, { init: { signal } })
  return await res.json()
}

export const currentDistributionMarginMixQueryOptions = (crtPeriod?: string) => ({
  queryKey: ['currentDistributionMarginMix', crtPeriod ?? null] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    getCurrentDistributionMarginMix(crtPeriod, signal),
})

export function useCurrentDistributionMarginMix(crtPeriod?: string) {
  return useQuery(currentDistributionMarginMixQueryOptions(crtPeriod))
}

async function getLatestDistributionMarginMix(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['distribution-margin-mix'].latest.$get({}, { init: { signal } })
  return await res.json()
}

export function useLatestDistributionMarginMix() {
  return useQuery({
    queryKey: ['latestDistributionMarginMix'] as const,
    queryFn: ({ signal }) => getLatestDistributionMarginMix(signal),
  })
}

async function getDistributionMarginMixList(
  search: DistributionMarginMixSearch,
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.crt['distribution-margin-mix'].$get(
    {
      query: stringifyValues(search),
    },
    { init: { signal } },
  )
  return await res.json()
}

async function getDistributionMarginMixById(
  id: string,
  queries: DistributionMarginMixDetailsSearch = {},
  signal?: AbortSignal,
) {
  const api = getApi()
  const { effectiveDateFrom, effectiveDateTo, ...rest } = queries
  const res = await api.crt['distribution-margin-mix'][':id'].$get(
    {
      param: { id },
      query: stringifyValues({
        ...rest,
        effectiveDateFrom: effectiveDateFrom ? formatDateOnly(effectiveDateFrom) : undefined,
        effectiveDateTo: effectiveDateTo ? formatDateOnly(effectiveDateTo) : undefined,
      }),
    },
    { init: { signal } },
  )
  return await res.json()
}

async function createDistributionMarginMix(payload: CreateDistributionMarginMix) {
  const api = getApi()
  const res = await api.crt['distribution-margin-mix'].$post({
    json: payload,
  })
  return await res.json()
}

async function updateDistributionMarginMix(id: number, details: UpdateDistributionMarginMix) {
  const api = getApi()
  await api.crt['distribution-margin-mix'][':id'].$patch({
    param: { id: String(id) },
    json: details,
  })
}

async function deleteDistributionMarginMix(ids: number[]) {
  const api = getApi()
  await api.crt['distribution-margin-mix'].bulk.$delete({
    json: { ids },
  })
}

export function useDistributionMarginMixList(queries: DistributionMarginMixSearch) {
  return useQuery({
    queryKey: ['distributionMarginMix', queries],
    queryFn: ({ signal }) => getDistributionMarginMixList(queries, signal),
  })
}

export const distributionMarginMixByIdQueryOptions = (
  id: string,
  queries: DistributionMarginMixDetailsSearch = {},
) => ({
  queryKey: ['distributionMarginMixById', id, queries],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    getDistributionMarginMixById(id, queries, signal),
})

export function useDistributionMarginMixById(
  id: string,
  queries: DistributionMarginMixDetailsSearch = {},
) {
  return useQuery(distributionMarginMixByIdQueryOptions(id, queries))
}

type MaterialGroup = NonNullable<ReturnType<typeof useMaterialGroups>['data']>[number]

export type DistributionMarginMixDetailRow = DistributionMarginMixWithDetails['details'][number] & {
  materialGroup: MaterialGroup | null
}

export type DistributionMarginMixViewModel = Omit<DistributionMarginMixWithDetails, 'details'> & {
  details: DistributionMarginMixDetailRow[]
}

export function useDistributionMarginMixDetailsViewModel(
  id: string,
  queries: DistributionMarginMixDetailsSearch = {},
) {
  const { data: materialGroups } = useMaterialGroups()

  const { materialGroupCode: materialGroupCodes, ...rest } = queries

  // Translate the material-group filter (codes) into the product hierarchy codes the backend uses.
  const productHierarchyCode = useMemo(
    () =>
      (materialGroups ?? [])
        .filter(
          (group) => materialGroupCodes?.includes(group.code) && group.productHierarchyCode != null,
        )
        .map((group) => String(group.productHierarchyCode)),
    [materialGroups, materialGroupCodes],
  )

  const query = useDistributionMarginMixById(id, {
    ...rest,
    productHierarchyCode: productHierarchyCode.length ? productHierarchyCode : undefined,
  })

  // Memoized so `data` keeps a stable identity across renders (this hook lives in a .ts file, which
  // the React Compiler does not process); otherwise consumers' effects on `data` would loop.
  const data = useMemo(() => {
    if (!query.data) return query.data

    const materialGroupByPhCode = new Map(
      (materialGroups ?? [])
        .filter((group) => group.productHierarchyCode != null)
        .map((group) => [group.productHierarchyCode, group] as const),
    )

    return {
      ...query.data,
      details: query.data.details.map((row) => ({
        ...row,
        materialGroup: materialGroupByPhCode.get(row.productHierarchyCode) ?? null,
      })),
    }
  }, [query.data, materialGroups])

  return { ...query, data }
}

async function getDistributionMarginMixDetailsForRawExport(id: string, signal?: AbortSignal) {
  const csvExport = await fetchCsvExport(
    `/crt/distribution-margin-mix/${id}/details/raw-export`,
    signal,
  )
  return csvExport
}

export const distributionMarginMixDetailsRawExportQueryOptions = (id: string) => ({
  queryKey: ['distributionMarginMixDetailsRawExport', id],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    getDistributionMarginMixDetailsForRawExport(id, signal),
})

async function getDistributionMarginMixDetailsForExport(
  id: string,
  queries: DistributionMarginMixDetailsSearch = {},
  signal?: AbortSignal,
) {
  const { effectiveDateFrom, effectiveDateTo, ...rest } = queries
  const stringifiedQueries = stringifyValues({
    ...rest,
    effectiveDateFrom: effectiveDateFrom ? formatDateOnly(effectiveDateFrom) : undefined,
    effectiveDateTo: effectiveDateTo ? formatDateOnly(effectiveDateTo) : undefined,
  })

  const filteredQueries = Object.fromEntries(
    Object.entries(stringifiedQueries).filter(([, value]) => value !== undefined),
  )
  const queryParams = new URLSearchParams(filteredQueries).toString()

  const csvExport = await fetchCsvExport(
    `/crt/distribution-margin-mix/${id}/details/export?${queryParams}`,
    signal,
  )
  return csvExport
}

export const distributionMarginMixDetailsExportQueryOptions = (
  id: string,
  queries: DistributionMarginMixDetailsSearch = {},
) => ({
  queryKey: ['distributionMarginMixDetailsExport', id, queries],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    getDistributionMarginMixDetailsForExport(id, queries, signal),
})

async function getDistributionMarginMixDistributors(id: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['distribution-margin-mix'][':id']['distributors'].$get(
    {
      param: { id },
    },
    { init: { signal } },
  )
  return await res.json()
}

export function useDistributionMarginMixDistributors(id: string) {
  return useQuery({
    queryKey: ['distributionMarginMixDistributors', id],
    queryFn: ({ signal }) => getDistributionMarginMixDistributors(id, signal),
  })
}

async function getDistributionMarginMixUnmatchedErps(id: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['distribution-margin-mix'][':id']['unmatched-erps'].$get(
    { param: { id } },
    { init: { signal } },
  )
  return await res.json()
}

export type DistributionMarginMixUnmatchedErp = Awaited<
  ReturnType<typeof getDistributionMarginMixUnmatchedErps>
>[number]

export function useDistributionMarginMixUnmatchedErps(id: string) {
  return useQuery({
    queryKey: ['distributionMarginMixUnmatchedErps', id],
    queryFn: ({ signal }) => getDistributionMarginMixUnmatchedErps(id, signal),
  })
}

export function useCreateDistributionMarginMix({ queries, handleOnSuccess }: UseCreateArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['distributionMarginMix', queries],
    mutationFn: createDistributionMarginMix,
    onSuccess: (record) => {
      handleOnSuccess(record.id)

      queryClient.invalidateQueries({
        queryKey: ['distributionMarginMix', queries],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('distributionMarginMix.createSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const customParams = issues?.find((i) => i.code === 'custom')?.params as
        | { type?: string; crtPeriod?: string }
        | undefined

      let description: string
      switch (customParams?.type) {
        case DISTRIBUTION_MARGIN_MIX_CREATE_ERROR_TYPES.crtPeriodConflict:
          description = `${t('errors.distributionMarginMix.createFailed', { ns: 'crt' })}: ${t(
            'errors.distributionMarginMix.crtPeriodConflict',
            { ns: 'crt', crtPeriod: customParams.crtPeriod },
          )}`
          break
        default:
          description = getFirstZodErrorMessage(
            issues,
            t,
            'errors.distributionMarginMix.createFailed',
            'crt',
          )
      }

      toast.error(t('toast.error', { ns: 'common' }), {
        description,
        className: toastClassName,
      })
    },
  })
}

export function useUpdateDistributionMarginMix({
  distributionMarginMixId,
  handleOnSuccess,
}: UseUpdateArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['updateDistributionMarginMix', distributionMarginMixId],
    mutationFn: (details: UpdateDistributionMarginMix) =>
      updateDistributionMarginMix(distributionMarginMixId, details),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['distributionMarginMixById', String(distributionMarginMixId)],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('distributionMarginMix.updateSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.distributionMarginMix.updateFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useDeleteDistributionMarginMix({
  distributionMarginMixIds,
  queries,
  handleOnSuccess,
}: UseDeleteArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['deleteDistributionMarginMix'],
    mutationFn: () => deleteDistributionMarginMix(distributionMarginMixIds),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['distributionMarginMix', queries],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('distributionMarginMix.deleteSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.distributionMarginMix.deleteFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}
